/** Roof pieces share the building grid's neighbour indices, and draw on the sky layer. */
export interface RoofAutotileDefinition {
    id: string
    name: string
    kind: "roof"
    tilesetId: string
    itemType: string
    tiles: Record<number, number>
    /**
     * Optional: a PITCHED roof. Its pieces are chosen from where each cell sits in the whole roof
     * rather than from its neighbours (see pitchedRoofPiece): piece name ("<row>.<edge>") -> tile.
     * When present, the game draws these and `tiles` only serves older tooling.
     */
    pitched?: Record<string, number>
    /**
     * Optional, pitched roofs only: named alternatives for some pitched pieces (a chimney on the
     * ridge, a dormer in the front slope, moss). `pieces` maps a pitched piece to the tile drawn
     * instead when the roof cell carries the ornament, and only those pieces can carry it. A cell
     * carries one when a player PLACES it (a chimney item marks a roof cell), or, for an ornament
     * with `scatter`, on that share of the cells whose piece it draws, picked by a hash of the cell
     * so editing a roof never reshuffles the others.
     */
    ornaments?: Record<string, RoofOrnament>
}

export interface RoofOrnament {
    pieces: Record<string, number>
    /** 0-1: the share of cells (of the pieces it draws) that carry it unprompted. Without it, the ornament is placed only. */
    scatter?: number
}

/**
 * A pitched roof's row roles, from the cell's north-south run of roof (length D, position i from
 * its north end): one row is `solo` (ridge and eave in one cell); up to four rows run ridgeTop,
 * front..., eave (the ridge on the first row, a glimpse of the back slope above it); five or more
 * run back, ridge, front..., eave -- when the whole ridge can drop a row (see pitchedRoofPiece).
 */
export const PITCHED_ROOF_ROWS = ["back", "ridgeTop", "ridge", "front", "eave", "solo"] as const
/** A pitched roof's edge position along its row: the gable edges, west and east, are rolled. */
export const PITCHED_ROOF_EDGES = ["mid", "west", "east", "single"] as const
/** Every piece a pitched roof needs, in sheet order (row-major: rows, then edges). */
export const PITCHED_ROOF_PIECES: string[] = PITCHED_ROOF_ROWS.flatMap(row => PITCHED_ROOF_EDGES.map(edge => `${row}.${edge}`))

/**
 * Optional pieces for where a taller part of a roof meets a lower roof's ridge (an L's wing, say):
 * a front-slope cell of the taller part whose neighbour on one side starts its own run there. Its
 * gable edge on that side carries on down to the lower ridge (JOIN_ROWS rows), so the ridge ends
 * against it instead of against bare slope. The other side stays as it would: continuing (westJoin,
 * eastJoin), a gable edge (westJoinEast: east gable; eastJoinWest: west gable), or a second join
 * (bothJoin). A material without them draws pitchedPieceFallback.
 */
export const PITCHED_ROOF_JOINS: string[] = ["front.westJoin", "front.eastJoin", "front.westJoinEast", "front.eastJoinWest", "front.bothJoin"]
/** How far down a join's gable edge runs: past the lowest ridge roll (rows 8-18 of a ridge piece). */
export const PITCHED_JOIN_ROWS = 19
/** The plain piece a join stands in for, for a material that has no join pieces. */
export const pitchedPieceFallback = (piece: string): string =>
    ({ "front.westJoin": "front.mid", "front.eastJoin": "front.mid", "front.westJoinEast": "front.east", "front.eastJoinWest": "front.west", "front.bothJoin": "front.mid" } as Record<string, string>)[piece] ?? piece

/** The rows of the north-south run of roof through (x, y). */
const columnRun = (isRoof: (x: number, y: number) => boolean, x: number, y: number) => {
    let top = y
    while (isRoof(x, top - 1)) top--
    let bottom = y
    while (isRoof(x, bottom + 1)) bottom++
    return { top, bottom }
}

/**
 * Whether the ridge along a roof's top edge can sit a row down, under a strip of back slope: only
 * when every column that starts on that edge (the east-west run of cells along row `top` with no
 * roof north of them) is five or more deep. Otherwise one shallow column would keep its ridge on
 * the top row while its deep neighbours dropped theirs, breaking the ridge -- a T, or an L with
 * its wing to the south, would show a patch of back slope in the middle of a broken ridge.
 */
const ridgeDrops = (isRoof: (x: number, y: number) => boolean, x: number, top: number): boolean => {
    const startsHere = (cx: number) => isRoof(cx, top) && !isRoof(cx, top - 1)
    const deep = (cx: number) => columnRun(isRoof, cx, top).bottom - top + 1 >= 5
    for (let cx = x; startsHere(cx); cx--) if (!deep(cx)) return false
    for (let cx = x + 1; startsHere(cx); cx++) if (!deep(cx)) return false
    return true
}

/**
 * The pitched piece for roof cell (x, y), given which cells are roof: its row role from its own
 * north-south run (so every column of a rectangle agrees, and an L or a courtyard adapts column by
 * column), with the ridge kept in one line along each top edge (ridgeDrops); its edge from whether
 * the row continues east and west.
 */
export const pitchedRoofPiece = (isRoof: (x: number, y: number) => boolean, x: number, y: number): string => {
    const { row, top } = pitchedRowRole(isRoof, x, y)
    const side = (nx: number): "open" | "join" | "continues" => {
        if (!isRoof(nx, y)) return "open"
        if (row !== "front") return "continues"
        // A taller part's front slope beside a lower roof starting here: its edge carries on down,
        // all the way beside the lower roof's back slope, to the bottom of the roll beside its ridge.
        const beside = pitchedRowRole(isRoof, nx, y)
        if (beside.top <= top) return "continues"
        if (beside.row === "back" && beside.top === y) return "open"
        return (beside.top === y && (beside.row === "ridgeTop" || beside.row === "solo")) || (beside.row === "ridge" && beside.top === y - 1) ? "join" : "continues"
    }
    const west = side(x - 1), east = side(x + 1)
    if (west === "join" || east === "join") {
        const edge = west === "join" && east === "join" ? "bothJoin" : west === "join" ? (east === "open" ? "westJoinEast" : "westJoin") : (west === "open" ? "eastJoinWest" : "eastJoin")
        return `front.${edge}`
    }
    return `${row}.${west === "open" && east === "open" ? "single" : west === "open" ? "west" : east === "open" ? "east" : "mid"}`
}

/** A cell's row role in its north-south run, and where the run starts. */
const pitchedRowRole = (isRoof: (x: number, y: number) => boolean, x: number, y: number) => {
    const { top, bottom } = columnRun(isRoof, x, y)
    const depth = bottom - top + 1
    const i = y - top
    const row = depth === 1 ? "solo"
        : depth <= 4 || !ridgeDrops(isRoof, x, top) ? (i === 0 ? "ridgeTop" : i === depth - 1 ? "eave" : "front")
        : (i === 0 ? "back" : i === 1 ? "ridge" : i === depth - 1 ? "eave" : "front")
    return { row, top }
}

/**
 * A stable number in [0, 1) for a roof cell, its storey and an ornament's name: the walls' mix
 * (wallOrnamentHash) over "roof:<name>", so a roof and a wall never share a draw. Restated here
 * because the roof kind imports nothing.
 */
export const roofOrnamentHash = (x: number, y: number, storey: number, name: string): number => {
    const salted = `roof:${name}`
    let h = 2166136261 ^ (x * 374761393) ^ (y * 668265263) ^ (storey * 2246822519)
    for (let i = 0; i < salted.length; i++) h = Math.imul(h ^ salted.charCodeAt(i), 16777619)
    h = Math.imul(h ^ (h >>> 15), 2246822507)
    h = Math.imul(h ^ (h >>> 13), 3266489909)
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

/** Whether a roof material draws ornament `name` on `piece`. */
export const roofOrnamentFits = (definition: RoofAutotileDefinition | undefined, name: string, piece: string): boolean =>
    !!definition?.pitched && definition.ornaments?.[name]?.pieces?.[piece] !== undefined

/**
 * The ornament a roof cell draws, given its pitched piece: the one placed on it, when its material
 * draws that ornament on that piece; otherwise the first scattered ornament (by name) that draws the
 * piece and whose hash falls under its share.
 */
export const roofOrnamentFor = (definition: RoofAutotileDefinition | undefined, placed: string | undefined, piece: string, x: number, y: number, storey = 0): string | undefined => {
    const ornaments = definition?.pitched && definition.ornaments
    if (!ornaments) return undefined
    if (placed && roofOrnamentFits(definition, placed, piece)) return placed
    return Object.keys(ornaments).sort().find(name => ornaments[name].pieces?.[piece] !== undefined &&
        (ornaments[name].scatter ?? 0) > 0 && roofOrnamentHash(x, y, storey, name) < ornaments[name].scatter)
}

/** The pitched piece a material draws at (x, y): pitchedRoofPiece, or its fallback when the material has no such join piece. */
export const drawnPitchedPiece = (definition: RoofAutotileDefinition, isRoof: (x: number, y: number) => boolean, x: number, y: number): string => {
    const piece = pitchedRoofPiece(isRoof, x, y)
    return definition.pitched?.[piece] !== undefined ? piece : pitchedPieceFallback(piece)
}

/** What a roof cell carries besides its material: an ornament placed on it, and its storey (scattered ornaments differ per storey). */
export interface RoofCellOptions {
    ornament?: string
    storey?: number
    /** World coordinate of local (0, 0); topology still uses local coordinates. */
    worldOrigin?: { x: number; y: number }
}

/**
 * The roof tile for a cell: a pitched material's piece by its place in the roof (or that piece's
 * ornament, placed or scattered), otherwise by its neighbour index.
 */
export const roofPieceTile = (definition: RoofAutotileDefinition | undefined, index: number, isRoof: (x: number, y: number) => boolean, x: number, y: number, options: RoofCellOptions = {}) => {
    if (!definition?.pitched) return roofCellTile(definition, index)
    const piece = drawnPitchedPiece(definition, isRoof, x, y)
    const ornament = roofOrnamentFor(definition, options.ornament, piece,
        x + (options.worldOrigin?.x ?? 0), y + (options.worldOrigin?.y ?? 0), options.storey ?? 0)
    return { tilesetId: definition.tilesetId, tilePos: ornament ? definition.ornaments[ornament].pieces[piece] : definition.pitched[piece] }
}

/** The original wooden roof mapping, preserved for existing roof-wood buildings. */
const woodenTiles: Record<number, number> = {47: 468, 42: 449, 36: 464, 46: 465, 12: 481, 28: 466, 34: 448, 26: 450, 7: 480, 4: 482, 18: 451, 8: 452, 15: 453, 14: 467, 6: 483, 3: 484, 5: 455, 2: 454, 13: 485, 1: 469, 21: 404, 9: 420, 19: 435, 16: 436, 29: 416, 37: 417, 17: 466, 22: 419, 20: 464, 10: 481, 27: 466, 35: 464, 11: 481}
export const roofHasTrigger = (index: number): boolean => !!index && ![18, 8, 15, 14, 6, 3, 34, 26, 42, 28, 13, 1, 5, 2].includes(index)
export const roofCellTile = (definition: RoofAutotileDefinition | undefined, index: number) => ({
    tilesetId: definition?.tilesetId || "fence_medieval",
    tilePos: definition ? definition.tiles[index] : woodenRoofTile(index),
})
export const woodenRoofTile = (index: number): number => woodenTiles[index] ?? 465
